import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, CheckCircle, AlertCircle, User } from 'lucide-react';
import clsx from 'clsx';
import { profileApi } from '../../../api/client';
import type { UserProfile } from '../../../api/client';
import { useAuthStore } from '../../../store/useAuthStore';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage',
  'America/Hawaii', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney',
];

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
];

function getInitials(profile: UserProfile | null): string {
  const name = profile?.display_name ?? profile?.full_name ?? profile?.email ?? '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || 'U';
}

export function ProfileSettingsPage() {
  const { user } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [locale, setLocale] = useState('en-US');

  // Avatar upload state
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    profileApi.get()
      .then((p) => {
        setProfile(p);
        setDisplayName(p.display_name ?? '');
        setFullName(p.full_name ?? '');
        setBio(p.bio ?? '');
        setJobTitle(p.job_title ?? '');
        setTimezone(p.timezone ?? 'UTC');
        setLocale(p.locale ?? 'en-US');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveStatus('idle');
    setSaveError('');
    try {
      const updated = await profileApi.update({
        display_name: displayName || null,
        full_name: fullName || null,
        bio: bio || null,
        job_title: jobTitle || null,
        timezone,
        locale,
      });
      setProfile(updated);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  function handleFileSelect(file: File) {
    setAvatarError('');
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setAvatarError('Unsupported format. Use PNG, JPG, or WebP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('Image exceeds 2 MB size limit.');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleAvatarUpload() {
    if (!avatarFile) return;
    setAvatarUploading(true);
    setAvatarError('');
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => {
          const result = e.target?.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(avatarFile);
      });
      const { avatarUrl } = await profileApi.uploadAvatar(avatarFile.name, avatarFile.type, base64);
      setProfile((p) => p ? { ...p, avatar_url: avatarUrl } : p);
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (err: unknown) {
      setAvatarError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleRemoveAvatar() {
    try {
      await profileApi.removeAvatar();
      setProfile((p) => p ? { ...p, avatar_url: null } : p);
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch {
      setAvatarError('Failed to remove avatar');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  const currentAvatar = avatarPreview ?? profile?.avatar_url ?? null;
  const initials = getInitials(profile);

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Profile</h2>
        <p className="text-sm text-gray-500 mt-1">Manage your identity and personal information.</p>
      </div>

      {/* Avatar Section */}
      <div className="op-panel p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Avatar</h3>
        <div className="flex items-start gap-6">
          {/* Avatar preview */}
          <div
            className={clsx(
              'relative w-20 h-20 rounded-full flex-shrink-0 overflow-hidden border-2 transition-colors cursor-pointer',
              dragOver ? 'border-brand' : 'border-surface-4',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file) handleFileSelect(file);
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {currentAvatar ? (
              <img src={currentAvatar} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-surface-3 flex items-center justify-center text-xl font-bold text-gray-400">
                {initials}
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />

          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-xs text-gray-500">
              PNG, JPG, or WebP · Max 2 MB · Click or drag to upload
            </p>
            {avatarError && (
              <p className="text-xs text-loss flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {avatarError}
              </p>
            )}
            <div className="flex items-center gap-2">
              {avatarFile && (
                <button
                  onClick={handleAvatarUpload}
                  disabled={avatarUploading}
                  className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  {avatarUploading && <Loader2 className="w-3 h-3 animate-spin" />}
                  Save Avatar
                </button>
              )}
              {avatarFile && (
                <button
                  onClick={() => { setAvatarFile(null); setAvatarPreview(null); }}
                  className="btn-ghost text-xs py-1.5 px-3"
                >
                  Cancel
                </button>
              )}
              {profile?.avatar_url && !avatarFile && (
                <button
                  onClick={handleRemoveAvatar}
                  className="text-xs text-loss hover:text-loss/80 transition-colors"
                >
                  Remove avatar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSave} className="op-panel p-4 space-y-5">
        <h3 className="text-sm font-medium text-gray-300">Personal Information</h3>

        {/* Email (read-only) */}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
          <div className="flex items-center gap-2 px-3 py-2 bg-surface-3 border border-surface-4 rounded-md">
            <User className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <span className="text-sm text-gray-500">{profile?.email ?? user?.email ?? '—'}</span>
            <span className="ml-auto text-xs text-gray-600">Read-only · Change in Security</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How you appear to teammates"
              maxLength={80}
              className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="First and last name"
              maxLength={120}
              className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Job Title</label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Sports Analyst"
            maxLength={100}
            className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Bio</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="A short description about yourself"
            maxLength={500}
            rows={3}
            className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand resize-none"
          />
          <p className="text-xs text-gray-600 mt-1">{bio.length}/500</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Language</label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {LOCALES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Account created date */}
        {profile?.created_at && (
          <p className="text-xs text-gray-600">
            Account created{' '}
            {new Date(profile.created_at).toLocaleDateString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric',
            })}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>

          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-sm text-win">
              <CheckCircle className="w-4 h-4" /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-sm text-loss">
              <AlertCircle className="w-4 h-4" /> {saveError || 'Failed to save'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
